'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { AddToCartButton } from '../add-to-cart-button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type ProductCardProps = {
  product: Product;
  inCarousel?: boolean;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className, inCarousel = false }: ProductCardProps) {
  const TitleTag = inCarousel ? 'h3' : 'h2';
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const { t } = useTranslations();
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const variants = useVariantsStore(state => state.product.variants);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);

  const disabled =
    (product.quantity < 1 && !sellable) || ((variants?.length || 0) > 0 && !selectedVariant);

  return (
    <div
      className={cn(
        'group transition-all',
        'col-span-1 flex w-full flex-col items-center justify-center gap-4 text-center',
        className?.main,
      )}
    >
      <Link
        prefetchOnHover
        prefetch={false}
        href={productUrl}
        className={cn(
          'relative w-full',
          inCarousel
            ? 'h-[400px] md:h-[468px]'
            : 'h-[215px] sm:h-[230px] md:h-[340px] lg:h-[440px]',
        )}
      >
        <Image
          src={image.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(image, product?.name)}
          fill
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          sizes="(max-width: 768px) 320px, 380px"
          quality={75}
        />
      </Link>
      <div
        className={cn(
          'relative overflow-y-hidden',
          'flex w-full flex-col items-center justify-center gap-2.5 text-center',
        )}
      >
        <TitleTag
          className={cn(
            'font-heading',
            'line-clamp-1 text-black transition-all duration-300 group-focus-within:md:-translate-y-16 group-hover:md:-translate-y-16',
            inCarousel ? 'text-lg xl:text-xl' : 'text-sm md:text-base lg:text-lg xl:text-xl',
          )}
        >
          <Link href={productUrl} prefetch={false} prefetchOnHover>
            {product.name}
          </Link>
        </TitleTag>
        {!priceDisplay.priceOnRequest ? (
          <div className="flex flex-col items-center transition-all duration-300 group-focus-within:md:-translate-y-16 group-hover:md:-translate-y-16">
            {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
              <span className="text-xs text-[#616161]">
                {t('text1926')}{' '}
                {formatPrice(
                  priceDisplay.mocPrice,
                  generalLayout.currency,
                  generalLayout.currency_settings,
                )}
              </span>
            ) : null}
            <div className="flex w-full items-baseline justify-center gap-2">
              <span
                className={cn(
                  'font-heading text-black',
                  inCarousel ? 'text-lg xl:text-xl' : 'text-base lg:text-lg xl:text-xl',
                )}
              >
                {formatPrice(
                  priceDisplay.current,
                  generalLayout.currency,
                  generalLayout.currency_settings,
                )}
              </span>
              {priceDisplay.showPreviousPrice && (
                <span
                  className={cn(
                    'font-heading text-[#616161] line-through',
                    inCarousel ? 'text-sm lg:text-base' : 'text-xs md:text-sm xl:text-base',
                  )}
                >
                  {formatPrice(
                    priceDisplay.beforeDiscount,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}
                </span>
              )}
            </div>
            {templateSettings.product_teaser?.price_without_vat?.enabled ? (
              <span className="text-xs text-[#616161]">
                {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                {formatPrice(
                  priceDisplay.secondary,
                  generalLayout.currency,
                  generalLayout.currency_settings,
                )}{' '}
                {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
              </span>
            ) : null}
          </div>
        ) : null}
        {/* One action tree: CSS changes placement, not the number of cart/query subscriptions. */}
        <div className="w-full transition-all duration-300 md:absolute md:translate-y-16 group-focus-within:md:translate-y-0 group-hover:md:translate-y-0">
          <div className="flex w-full items-center justify-center gap-3">
            <AddToCartButton
              product={product}
              quantity={selectedQuantity}
              disabled={disabled}
              sellable={sellable}
              iconOnly
              size="icon"
              variant="ghost"
              className={cn('ea-product-card__add-to-cart-button')}
            />
            {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
              <AddToFavoritesButton
                slug={product.slug}
                isFavorite={product.is_favorite ?? false}
                productId={product.id}
                buttonVariant="ghost"
                buttonSize="icon"
                buttonClassName={cn('ea-product-card__favorite-button')}
                className="h-6 w-6"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider state={{ product: props.product }}>
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
